import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody, Chip, Spinner } from '@nextui-org/react';
import { ArrowLeft, BookOpen, ChevronDown, HelpCircle } from 'lucide-react';
import infoPageService, { InfoPage } from '../services/infoPageService';

const TEAL = '#3FCED2';

/**
 * /info/<slug> – Detailseite einer Informationsseite:
 * Hero mit Banner + Intro, Eckdaten, Inhaltssektionen, FAQ (aufklappbar).
 * Stil nach dem Vorbild der PDC-Turnier-Detailseite.
 */
const InfoPageDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<InfoPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    infoPageService
      .getPageBySlug(slug)
      .then((result) => {
        if (cancelled) return;
        if (!result) setNotFound(true);
        setPage(result);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const sections = useMemo(
    () => (page?.sections ?? [])
      .filter((s) => s && s.heading && s.body)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [page],
  );
  const faq = useMemo(
    () => (page?.faq ?? [])
      .filter((f) => f && f.question && f.answer)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [page],
  );
  const keyFacts = useMemo(
    () => (page?.keyFacts ?? [])
      .filter((f) => f && f.label && f.value)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [page],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <div className="flex h-64 items-center justify-center">
          <Spinner size="lg" style={{ color: TEAL }} />
        </div>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Seite nicht gefunden | DartCloud</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center">
          <BookOpen size={64} className="mx-auto mb-4 text-gray-600" />
          <h1 className="mb-2 text-2xl font-bold text-white">Seite nicht gefunden</h1>
          <p className="mb-6 text-gray-400">Diese Info-Seite existiert nicht (mehr).</p>
          <Link to="/info" className="font-semibold underline" style={{ color: TEAL }}>
            Zurück zum Darts-Wissen
          </Link>
        </div>
      </div>
    );
  }

  const bannerUrl = infoPageService.buildFileUrl(page.heroImage || page.thumbnail);
  const pageTitle = page.seoTitle || `${page.title} | DartCloud`;
  const pageDescription = page.seoDescription || page.intro || '';

  // FAQ-Schema für Google (Rich Results)
  const faqJsonLd = faq.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`https://dartcloudapp.de/info/${page.slug}`} />
        {faqJsonLd && (
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        )}
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-8">
        <Link
          to="/info"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white"
        >
          <ArrowLeft size={15} /> Darts-Wissen
        </Link>

        {/* Hero */}
        <div className="relative mb-6 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
          {bannerUrl && (
            <>
              <img src={bannerUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.72) 55%, rgba(0,0,0,0.45) 100%)',
                }}
              />
            </>
          )}
          <div className={`relative p-5 sm:p-8 ${bannerUrl ? 'flex min-h-[220px] flex-col justify-end' : ''}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {page.category && (
                <Chip size="sm" variant="flat" style={{ backgroundColor: `${TEAL}33`, color: TEAL }}>
                  {page.category}
                </Chip>
              )}
              <Chip size="sm" variant="flat" className="bg-zinc-800/80 text-gray-300">
                Darts-Wissen
              </Chip>
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white md:text-4xl">
              {page.title}
            </h1>
            {page.intro && (
              <p className="mt-3 max-w-3xl text-sm text-gray-300 md:text-base">{page.intro}</p>
            )}
          </div>
        </div>

        {/* Eckdaten */}
        {keyFacts.length > 0 && (
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            {keyFacts.slice(0, 8).map((fact, idx) => (
              <Card key={`${fact.label}-${idx}`} className="border-zinc-800 bg-zinc-900">
                <CardBody className="p-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    {fact.label}
                  </p>
                  <p className="mt-1 text-sm font-bold text-white md:text-base">{fact.value}</p>
                  {fact.sublabel && (
                    <p className="mt-0.5 text-[11px] text-gray-500">{fact.sublabel}</p>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        )}

        {/* Inhaltssektionen */}
        <div className="space-y-6">
          {sections.map((section, idx) => (
            <Card key={`${section.heading}-${idx}`} className="border-zinc-800 bg-zinc-900">
              <CardBody className="p-5 sm:p-6">
                <h2 className="mb-3 text-lg font-bold text-white md:text-xl">{section.heading}</h2>
                <div className="space-y-3 text-sm leading-relaxed text-gray-300 md:text-[15px]">
                  {(section.body || '')
                    .split(/\n{2,}|\n/)
                    .filter((p) => p.trim())
                    .map((paragraph, pIdx) => (
                      <p key={pIdx}>{paragraph}</p>
                    ))}
                </div>
              </CardBody>
            </Card>
          ))}

          {sections.length === 0 && (
            <Card className="border-zinc-800 bg-zinc-900">
              <CardBody className="p-8 text-center">
                <BookOpen size={40} className="mx-auto mb-3 text-zinc-700" />
                <p className="text-gray-400">Der Inhalt dieser Seite wird gerade erstellt.</p>
              </CardBody>
            </Card>
          )}
        </div>

        {/* FAQ */}
        {faq.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-white">
              <HelpCircle size={20} style={{ color: TEAL }} />
              Häufige Fragen
            </h2>
            <div className="space-y-2">
              {faq.map((item, idx) => {
                const open = openFaq === idx;
                return (
                  <Card key={`${item.question}-${idx}`} className="border-zinc-800 bg-zinc-900">
                    <CardBody className="p-0">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(open ? null : idx)}
                        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                      >
                        <span className="text-sm font-semibold text-white md:text-base">
                          {item.question}
                        </span>
                        <ChevronDown
                          size={18}
                          className={`shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {open && (
                        <div className="border-t border-zinc-800 px-5 py-4 text-sm leading-relaxed text-gray-300">
                          {item.answer}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoPageDetailPage;
